import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Ban, Bot, ClipboardList, FileText, FlaskConical, Layers, Loader2, Pause,
  RefreshCw, RotateCcw, Sprout,
} from 'lucide-react';
import { agenticAPI } from '../../../../services/api';
import ReportView from './ReportView';
import AgentRail, { HYPOTHESIS_META } from './AgentRail';
import DecisionDock from './DecisionDock';
import EvidencePane from './EvidencePane';
import NextStep, { nextStep } from './NextStep';
import { LivePulse, TurnClock, useTicker } from './Liveness';
import PlanView, { PLAN_DECISION_ID } from './PlanView';
import StreamView from './StreamView';
import TeamRoster from './TeamRoster';

/**
 * The bench.
 *
 * One engagement header, a rail of hypotheses, and the selected hypothesis's evidence
 * filling the screen. The evidence is the hero rather than a status card,
 * because status is a project-management concept and what a developer will
 * argue with is the request pair.
 *
 * Polls while anything is in flight and stops when nothing is. An agent turn
 * takes minutes, so a fixed fast interval would be mostly wasted requests, and
 * no polling at all would leave the operator staring at a stale screen during
 * the one part they actually want to watch.
 */

//: How often to re-read while the runtime is working.
//
//: 8s was chosen when a turn was the only thing that ever changed. It is far
//: too slow now: recon lands in 1-3 seconds, so the operator approved a plan,
//: watched a stale banner for most of a poll cycle, and reached for refresh.
//: The whole first minute of an engagement is the part that has to feel alive.
const POLL_MS = 2500;

//: And once nothing is in flight, back off. This only runs inside the
//: after-action window, where the point is to catch the first sign of life
//: rather than to watch a quiet engagement.
const IDLE_POLL_MS = 8000;

//: How long to keep polling after the operator asks for something.
//:
//: Advance and Seed queue hand the work to Celery and return immediately, so
//: the button stops spinning while the job has not started. Polling was gated
//: on `inFlight`, which is derived from runs that ALREADY exist, so on an
//: engagement with nothing running there was nothing to poll for and the
//: screen sat still until the operator reloaded the browser. That reads as
//: "the button did nothing", which is the one thing it must never read as.
const AFTER_ACTION_POLL_MS = 90_000;

// Plan first: it is the gate everything else waits behind, and on a fresh
// engagement it is the only tab with anything in it.
const TABS = [
  { key: 'plan', label: 'Plan', icon: ClipboardList },
  { key: 'bench', label: 'Angles', icon: FlaskConical },
  { key: 'stream', label: 'Stream', icon: Layers },
  { key: 'findings', label: 'Findings', icon: AlertTriangle },
  { key: 'report', label: 'Report', icon: FileText },
];

function Meter({ label, used, cap }) {
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return (
    <div className="min-w-[6rem]">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full ${pct >= 90 ? 'bg-amber-500' : 'bg-wasabi-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-0.5 font-mono text-[10px] text-slate-500">
        {used}/{cap}
      </div>
    </div>
  );
}

export default function Bench({ pentest }) {
  const [overview, setOverview] = useState(null);
  const [hypotheses, setHypotheses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  //: When the operator last asked for something. Drives the poll window above.
  const [askedAt, setAskedAt] = useState(0);
  const [waiting, setWaiting] = useState(false);
  //: A transient, non-error message. `error` is red and sticky and wrong for
  //: "the runtime is already busy", which is reassurance rather than a fault.
  //
  //: AND IT HAS TO EXPIRE. It did not, so "Already working on it" stayed on
  //: screen after the turn landed, after the plan came back for approval, and
  //: until the operator pressed F5 - at which point it vanished, proving it had
  //: been stale the whole time. A reassurance that outlives the thing it is
  //: reassuring about is just another lie on the screen.
  const [notice, setNotice] = useState(null);
  const [noticeAt, setNoticeAt] = useState(0);

  //: Every plan version this engagement has had. Read for its LENGTH: "three
  //: plans" is how many times the Lead has been round the loop, which is the
  //: number the operator asked for and the one thing on this screen that says
  //: an engagement is iterating rather than stuck.
  const [plans, setPlans] = useState([]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [ov, ls] = await Promise.all([
        agenticAPI.overview(pentest.id),
        agenticAPI.listHypotheses(pentest.id),
      ]);
      setOverview(ov);
      setHypotheses(ls);
      return ls;
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? 'The agent runtime is available to Red Teamers only.'
          : (e?.response?.data?.detail || 'Could not load the engagement.'),
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [pentest.id]);

  useEffect(() => { load(); }, [load]);

  //: OUTSIDE `load`, AND THAT IS THE POINT.
  //
  //: The first version of this called planVersions inside load's try block,
  //: under the wrong name (`agenticAPI.plans`, which does not exist). The
  //: TypeError was caught by load's own handler and rendered as "Could not
  //: load the engagement" over a completely healthy screen. A decorative count
  //: must not be able to fail the thing it decorates, so it gets its own
  //: effect and swallows its own errors: an engagement with no plans yet is
  //: the ordinary case, and the header falls back to the newest plan's version.
  //
  //: Found by the browser suite on its first real run.
  useEffect(() => {
    let cancelled = false;
    agenticAPI.planVersions(pentest.id)
      .then((rows) => { if (!cancelled) setPlans(rows || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pentest.id, askedAt]);

  // Land on the tab that has something in it. A plan waiting on the operator is
  // the whole engagement stopped, and an engagement with no hypotheses yet has
  // an empty Bench, so defaulting to Bench would show a blank screen at exactly
  // the two moments there is something to do.
  //: THE PIN IS SCOPED TO A STAGE, NOT TO THE SESSION, and that is the whole
  //: fix for four separate refresh reports (#2, #6, #10, #14).
  //
  //: A plain boolean was correct about one thing and disastrous about another.
  //: Correct: once the operator picks a tab, the screen must not move under
  //: them. Disastrous: reviewing the plan is how every engagement starts, so
  //: the pin was ALWAYS set within seconds, and the auto-landing that was
  //: supposed to follow the engagement never ran again. Approving a test plan
  //: is the single most important transition on this screen and it was the one
  //: guaranteed to need an F5.
  //
  //: So the pin records WHICH STAGE it was made in. Staying put while the
  //: engagement is in the same stage is respecting the operator's choice;
  //: staying put after the engagement has moved to a different stage is just
  //: showing them a screen that no longer describes anything.
  const [pinnedAt, setPinnedAt] = useState(null);

  //: A coarse identity for "where the engagement is". Deliberately includes
  //: the plan VERSION and STATUS: a new plan version, or the same plan being
  //: approved, are both moments the interesting content changes tab.
  const stageId = useMemo(() => {
    if (!overview) return null;
    const p = overview.plan;
    return [
      p ? `plan:${p.version}:${p.status}` : 'plan:none',
      hypotheses.length ? 'angles' : 'no-angles',
      overview.awaiting_operator?.length ? 'reported' : '',
      (overview.roster || []).find((m) => m.role === 'lead')?.status || 'no-lead',
    ].join('|');
  }, [overview, hypotheses.length]);

  useEffect(() => {
    if (!overview) return;
    // The operator is still in the stage they chose a tab in. Leave them alone.
    if (pinnedAt && pinnedAt === stageId) return;
    const awaiting = overview.plan?.status === 'awaiting_approval';
    // RE-EVALUATED, not decided once. The old guard was `if (tab) return`, so
    // the first landing chose a tab for the rest of the session: an operator
    // who approved a plan stayed on Plan while the angles filled in behind
    // them, and only a page refresh moved them on. Approving a plan IS the
    // moment the interesting content changes tab, so it has to be followed.
    const next = awaiting || !hypotheses.length ? 'plan' : 'bench';
    setTab((current) => (current === next ? current : next));
    // The stage has moved on, so the old pin is spent. Clearing it here rather
    // than leaving it stale means the NEXT stage change is also followed.
    if (pinnedAt) setPinnedAt(null);
  }, [pinnedAt, stageId, overview, hypotheses.length]);

  const chooseTab = (next) => {
    setPinnedAt(stageId || 'unknown');
    setTab(next);
  };

  // Select something sensible on first load: whatever is being worked, else
  // whatever was confirmed, else the top of the queue.
  useEffect(() => {
    if (selectedId || !hypotheses.length) return;
    const working = hypotheses.find((l) => l.status === 'investigating');
    const confirmed = hypotheses.find((l) => l.status === 'confirmed');
    setSelectedId((working || confirmed || hypotheses[0]).id);
  }, [hypotheses, selectedId]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      setDetail(await agenticAPI.hypothesisDetail(pentest.id, selectedId));
    } catch {
      setDetail(null);
    }
  }, [pentest.id, selectedId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  // WHAT COUNTS AS "SOMETHING IS GOING TO CHANGE".
  //
  // This used to be "a run is running or a hypothesis is investigating", and
  // it switched polling off in the two windows where the screen most needed
  // to move: between approving a plan and the first worker turn (hypotheses
  // are `queued`, no run exists yet), and while the Lead is parked waiting to
  // be read. Watched both: the operator approved a plan, the tab sat still,
  // and a refresh showed work had been running for minutes.
  //
  // `driving` is the backend's own answer to this question - it is the flag
  // the scheduler itself checks before advancing an engagement - so keying on
  // it means the screen stops exactly when the runtime does and not before.
  // The other two terms stay as a belt-and-braces for the moment between an
  // operator action and the flag being re-read.
  // The SAME pure function the banner renders from, so the two can never
  // disagree about which state the engagement is in. Deriving it twice from
  // different reads is how a screen ends up with two buttons or none.
  const step = useMemo(
    () => nextStep({ overview, hypotheses }),
    [overview, hypotheses],
  );

  //: THE FIRST TEN SECONDS, WHICH IS WHERE THE TRUST IS WON OR LOST.
  //
  //: Pressing Start dispatches a Celery task and returns. The Lead run row
  //: does not exist yet, so the banner still reads "Nothing has started yet"
  //: and the play button is still sitting there. A model turn is 20-60s, so
  //: that is up to a minute of a screen that looks like it ignored the click.
  //: Nothing is broken and it is indistinguishable from broken.
  //:
  //: So the UI asserts what it just asked for, until the server shows
  //: something. Bounded: if 45s pass with no sign of life the claim expires
  //: rather than becoming its own lie, which is the failure mode every other
  //: label on this screen has already had.
  const PENDING_MS = 45_000;
  const [pendingSince, setPendingSince] = useState(0);
  const pending = useMemo(() => {
    if (!pendingSince) return false;
    if (Date.now() - pendingSince > PENDING_MS) return false;
    // Something real happened: stop asserting and show the truth.
    return !(overview?.roster || []).some((m) => m.role === 'lead');
  }, [pendingSince, overview]);

  const inFlight = useMemo(
    () =>
      overview?.driving === true ||
      (overview?.active?.length || 0) > 0 ||
      hypotheses.some((l) => ['investigating', 'queued'].includes(l.status)),
    [overview, hypotheses],
  );

  //: HOW LONG "ALREADY WORKING ON IT" IS ALLOWED TO CLAIM ANYTHING.
  //
  //: An advisory lock was measured held for 93 seconds on pentest 45, so the
  //: window has to cover a long turn. Past that the claim expires on its own
  //: rather than becoming its own lie, which is the same bound and the same
  //: reason as `pendingSince`.
  const NOTICE_MS = 100_000;

  //: AND IT ALSO CLEARS THE MOMENT THE ENGAGEMENT MOVES, which is the case the
  //: operator actually hit: the turn landed, the Lead came back with a plan
  //: awaiting approval, and the screen was still telling him a turn was
  //: running. `stageId` changing is the runtime saying the thing the notice
  //: was about is over.
  useEffect(() => {
    if (!notice) return undefined;
    setNotice(null);
    setNoticeAt(0);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageId]);

  useEffect(() => {
    if (!noticeAt) return undefined;
    const id = setTimeout(() => { setNotice(null); setNoticeAt(0); },
      Math.max(0, NOTICE_MS - (Date.now() - noticeAt)));
    return () => clearTimeout(id);
  }, [noticeAt]);

  //: THE BUTTON MUST NOT OFFER TO DO THE THING IT JUST SAID IS ALREADY HAPPENING.
  //
  //: The operator's first item, with a screenshot: the notice read "Already
  //: working on it" while Run next step sat beside it, green and clickable. So
  //: the screen contradicted itself and invited exactly the repeated pressing
  //: the notice exists to explain.
  const advancing = !!noticeAt && Date.now() - noticeAt < NOTICE_MS;

  //: A one-second clock, running ONLY while something is in flight. This is
  //: what makes a turn counter readable: see Liveness.js.
  const now = useTicker(inFlight || advancing);

  // Poll while something is running, AND for a while after the operator asks
  // for something, because the thing they asked for does not exist yet at the
  // moment they ask for it.
  useEffect(() => {
    const recentlyAsked = askedAt && Date.now() - askedAt < AFTER_ACTION_POLL_MS;
    if (!inFlight && !recentlyAsked && !pending) {
      setWaiting(false);
      return undefined;
    }
    setWaiting(!inFlight);
    const id = setInterval(() => {
      if (askedAt && Date.now() - askedAt >= AFTER_ACTION_POLL_MS && !inFlight) {
        setWaiting(false);
        setAskedAt(0);
        return;
      }
      load();
      loadDetail();
    }, inFlight ? POLL_MS : IDLE_POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, askedAt, pending, load, loadDetail]);

  // REFRESH ON FOCUS. `setInterval` is throttled or paused outright while the
  // tab is backgrounded, which the poll above has no defence against: an
  // operator who tabbed away mid-run came back to a screen that still claimed
  // whatever was true when they left, and had to press refresh by hand to
  // learn a run had gone out of budget in the meantime. `visibilitychange`
  // fires the moment the tab is looked at again regardless of what the
  // throttled timer thinks the time is, and `focus` covers the same return by
  // window rather than by tab. Both call the same loaders the poll already
  // uses, so this is a catch-up read, not a second polling loop.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      load();
      loadDetail();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [load, loadDetail]);

  const act = async (name, fn) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      // "ALREADY DOING IT" IS NOT "NOTHING TO DO". A slice holds an advisory
      // lock for the length of a turn, and a second dispatch stands down. The
      // endpoint used to report that as an ordinary queue, so the operator
      // pressed Run next step during a model call, was told work was queued,
      // and watched nothing happen. Measured on pentest 45: 93 seconds of
      // that. Say which it was.
      // THE UI WRITES THIS SENTENCE, NOT THE RUNTIME. `res.detail` reads
      // "already advancing, this slice stands down", which is accurate and is
      // vocabulary from inside the scheduler: a slice is a Celery task holding
      // an advisory lock. The operator's report was simply that he could not
      // tell whether that meant good or bad. What he needs to know is that his
      // press was not lost and that there is nothing to do.
      if (res?.advancing) {
        setNotice(
          'Already working on it. A turn is running right now, so this press '
          + 'was not needed and nothing was lost. The screen updates itself '
          + 'when the turn lands.',
        );
        setNoticeAt(Date.now());
      }
      if (name === 'advance') setPendingSince(Date.now());
      // The request returning means the job is QUEUED, not done. Start the
      // poll window here so the screen catches up on its own.
      setAskedAt(Date.now());
      await load();
      await loadDetail();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : `Could not ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  // `tab` is null for the render between the load finishing and the effect
  // above choosing a tab. Showing the spinner through that beat avoids a frame
  // of the Bench layout before landing somewhere else.
  if (loading || (!tab && !error)) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the engagement...
      </div>
    );
  }

  const counts = overview?.counts || {};
  const confirmed = hypotheses.filter((l) => l.status === 'confirmed');
  const activeRun = detail?.runs?.[detail.runs.length - 1];

  return (
    // WORKSPACE CANVAS. The Bench sits on a soft slate panel that fills the
    // viewport, with the white cards on top. Content shorter than a tall
    // monitor no longer ends in raw white background - the empty area below is
    // the workspace, which is what the operator asked for after that gap was
    // flagged repeatedly. min-h is viewport-minus-chrome (app nav + hero + tab
    // strip ~ 15rem); slightly under on purpose, since overshooting adds an
    // empty scroll region.
    <div className="-mx-4 -mb-4 min-h-[calc(100vh-15rem)] space-y-4 rounded-t-xl bg-slate-50 px-4 pb-8 pt-4 sm:-mx-6 sm:px-6">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-gradient-to-br from-wasabi-500 to-wasabi-700 p-2">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Autonomous testing</h3>
            <p className="text-xs text-slate-500">
              floor <span className="font-semibold">{overview?.exploit_level_floor}</span>
              {' '}&middot;{' '}
              <span title="How many agents may work this target at once, from the rules of engagement. The Lead is not one of them: it never touches a host.">
                {(overview?.max_concurrent_agents ?? 0) - (overview?.concurrency_slots_free ?? 0)}
                {' '}of {overview?.max_concurrent_agents ?? 0} agents on the target
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* THE LIVE ONE PULSES. "1 Under test" and "6 Refuted" were
              rendered identically, so the single row that is actually moving
              looked like the five that had stopped. The operator asked for
              exactly this: "we need to visualize things that are being worked
              on [...] we need to let the user know that things are
              happening." */}
          {Object.entries(HYPOTHESIS_META).map(([key, meta]) =>
            counts[key] ? (
              <span key={key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                {key === 'investigating'
                  ? <LivePulse tone={meta.dot} />
                  : <span className={`h-2 w-2 rounded-full ${meta.dot}`} />}
                {counts[key]} {meta.label}
              </span>
            ) : null,
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!!busy}
            onClick={() => act('seed', () => agenticAPI.seed(pentest.id))}
            // THE OPERATOR ASKED WHAT THIS BUTTON DOES, which is the answer.
            // The label named its input and never its output, so "Propose from
            // findings" read as a report action rather than as the second way
            // to start an engagement.
            title={'Turns what this engagement ALREADY knows into angles to test: one '
              + 'per existing finding, plus one per scope target. Nothing runs and '
              + 'nothing touches a host - they arrive as proposals that still need a '
              + 'plan and your approval. Use it when a previous scan or a manual test '
              + 'already found something worth chasing; on an engagement with no '
              + 'findings, Run next step and let the Lead do recon instead.'}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy === 'seed' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sprout className="h-3.5 w-3.5" />}
            Propose angles
          </button>
          {/* THE HEADER NO LONGER CARRIES "Run next step". It was a second
              button with the same label calling the same act('advance'), sitting
              directly above the one in the NextStep banner.

              They were not redundant by design, they drifted. This one was
              permanent and said the same thing whatever the engagement needed;
              the banner one is DERIVED, and renders the single action that is
              actually next, which is sometimes advance, sometimes "Review the
              plan" on another tab, sometimes "Read the report", and sometimes
              nothing at all because the honest answer is wait. Keeping both
              meant the header could offer "Run next step" while the banner was
              saying a plan needs approving: one button, two different right
              answers.

              The escape hatch is intact. Every state where advancing would
              actually do something is a state where the banner offers it,
              including the `!driving` branch that appears when nothing is
              scheduled. Where the banner shows a spinner instead, advancing is
              a no-op anyway: the advisory lock makes a duplicate slice stand
              down. */}
          {/* Shown only while the engagement is actually being driven, because
              a Pause on a paused engagement is a control that does nothing and
              a question the operator has to answer for themselves.

              The control the operator never had. Once the scheduler drives
              itself the only ways to stop were to wait for every run to park or
              to reset, and reset deletes the agentic work. This leaves every
              row where it is; Run next step starts it again. */}
          {overview?.driving && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() => act('pause', () => agenticAPI.pause(pentest.id))}
              title="Stop advancing this engagement. Nothing is lost and nothing is deleted."
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === 'pause' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />}
              Pause
            </button>
          )}
          <button
            type="button"
            onClick={() => { load(); loadDetail(); }}
            title="Refresh"
            className="rounded-md border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${inFlight ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <NextStep
        overview={overview}
        hypotheses={hypotheses}
        plans={plans}
        tab={tab}
        // `advancing` counts as busy. See the note on `advancing` above: a
        // green button beside "Already working on it" is the screen arguing
        // with itself.
        busy={!!busy || advancing}
        // `pending` WAS COMPUTED AND NEVER PASSED, so the optimistic "Waking
        // the Lead" state this component carries has never once rendered. The
        // whole point of it is the first ten seconds after Start, when the run
        // row does not exist yet and the banner otherwise re-offers the button
        // the operator just pressed. Dead code that read as a live feature.
        pending={pending}
        // chooseTab, NOT setTab. Setting the tab without pinning it meant the
        // landing effect re-ran on the next poll and pushed the operator
        // straight back: pressing "Read the report" showed the report for one
        // poll cycle and then bounced to Angles.
        onTab={chooseTab}
        onAct={(name) => act(name, () => agenticAPI[name](pentest.id))}
      />

      <DecisionDock
        pentestId={pentest.id}
        overview={overview}
        onSelectHypothesis={(id) => { setSelectedId(id); chooseTab('bench'); }}
        onOpenPlan={() => {
          // Refresh on the way there, because the tab may be showing a stale
          // render of an engagement that had no plan when it loaded.
          chooseTab('plan');
          load();
          // AND SCROLL TO THE DECISION. Setting the tab alone did nothing
          // whatsoever when the operator was already on it, which is the
          // ordinary case since the dock is what sends them here. The button
          // read as broken because for them it was.
          //
          // After a frame, so the tab has rendered and the node exists. Two
          // frames, in fact: the first commits the tab switch, the second lets
          // the plan lay out, and scrolling between them lands short.
          requestAnimationFrame(() => requestAnimationFrame(() => {
            document
              .getElementById(PLAN_DECISION_ID)
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }));
        }}
        onChanged={async () => { await load(); await loadDetail(); }}
      />

      {waiting && (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
          <span>
            Queued. This runs in the background and can take a minute or two;
            the screen updates itself.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {notice && !error && (
        <div
          data-testid="bench-notice"
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 motion-safe:animate-panel-arrive"
        >
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-wasabi-600" />
          <span>{notice}</span>
        </div>
      )}

      {/* The "Plan vN is waiting on you" strip lived here and said the same
          thing as the NextStep banner directly above it, which also carries a
          button that goes to the same place. NextStep is the richer of the two
          and is the component whose whole job is naming the single next action,
          so it keeps the job and this is gone. */}

      {/* tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            // Two hooks, and the second is the one that matters: a test asking
            // "where did the operator end up" must read the ACTIVE tab, not
            // search the page for a word that also appears in the banner.
            data-testid={tab === key ? 'bench-tab-active' : `bench-tab-${key}`}
            aria-current={tab === key ? 'page' : undefined}
            onClick={() => chooseTab(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm transition
              ${tab === key
                ? 'border-b-2 border-wasabi-500 font-semibold text-wasabi-700'
                : 'text-slate-500 hover:text-slate-800'}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            {key === 'findings' && confirmed.length > 0 && (
              <span className="rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-700">
                {confirmed.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'plan' ? (
        // min-h-[60vh], THE SAME FLOOR THE OTHER TABS SET ON THEMSELVES BELOW.
        //
        // The canvas this sits in has its own min-height, but that is a
        // budget shared by every sub-tab, and Bench/Stream/Findings already
        // needed their own row-level min-h-[60vh] on top of it (see the
        // comment on that row) before the slate reliably reached the bottom
        // of the viewport. Plan and Report never got the same floor. Plan is
        // the one that surfaces it most: a fresh engagement's "No plan yet"
        // card, or a short recon-only plan, is shallow content, and shallow
        // content is exactly when the canvas's own budget can fall short of
        // the real viewport and leave a strip of plain page below the slate.
        // Giving Plan the identical floor is the same fix already proven
        // here, not a new one.
        <div className="min-h-[60vh]">
          <PlanView
            pentestId={pentest.id}
            pentestName={pentest.name}
            roster={overview?.roster}
            planSummary={overview?.plan}
            angles={hypotheses}
            // Passed ONLY when the banner has stood its own action down, so the
            // two can never both be on screen. See NextStep's startsBelow.
            onStart={step?.startsBelow
              ? () => act('advance', () => agenticAPI.advance(pentest.id))
              : undefined}
            starting={busy === 'advance'}
            onChanged={async () => {
              // Every action on that screen hands work to Celery: running the
              // next step, approving, sending back. Opening the poll window here
              // covers all of them, and is why the plan now appears on its own.
              setAskedAt(Date.now());
              await load();
              await loadDetail();
            }}
          />
        </div>
      ) : tab === 'report' ? (
        <ReportView
          pentestId={pentest.id}
          // The report used to assemble itself from whatever the ledger held at
          // the moment the tab was opened, with nothing on the screen saying
          // the engagement was still running. It needs the engagement state to
          // know whether it is allowed to hand anything over.
          overview={overview}
          hypotheses={hypotheses}
          plans={plans}
          onOpenPlan={() => chooseTab('plan')}
        />
      ) : (
      <div className="flex min-h-[60vh] gap-4">
        {/* min-h so a short right column does not leave the page half empty on
            a tall monitor: the operator flagged that white space repeatedly.
            The rail (angles + agents) is often taller than the content, so the
            row fills to a viewport fraction and the empty states below center
            within it rather than sitting as a stunted box at the top. */}
        <aside className="w-56 shrink-0 space-y-4">
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Angles
            </div>
            <AgentRail
              hypotheses={hypotheses}
              // The rail shows progress per angle now, and progress lives on
              // the agent working it rather than on the angle itself.
              roster={overview?.roster}
              selectedId={selectedId}
              onSelect={setSelectedId}
              now={now}
            />
          </div>
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Agents
            </div>
            <TeamRoster
              roster={overview?.roster}
              pentestId={pentest.id}
              onChanged={load}
              now={now}
            />
          </div>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          {!detail ? (
            <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Select an angle.
            </div>
          ) : (
            <>
              <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-slate-900">{detail.title}</h4>
                    <p className="mt-0.5 text-xs leading-snug text-slate-600">{detail.hypothesis}</p>
                  </div>
                  <div className="flex shrink-0 items-start gap-3">
                    {/* REOPEN. A settled angle used to be a dead end for the
                        operator: retask was a Lead action only, so knowing an
                        angle had been blocked for a reason since fixed left
                        nothing to do but reset the whole engagement. Offered
                        only on the three states that mean "we stopped"; the
                        API refuses confirmed and investigating anyway. */}
                    {['blocked', 'refuted', 'abandoned'].includes(detail.status) && (
                      <button
                        type="button"
                        disabled={busy === 'retask'}
                        onClick={() => act('retask', () =>
                          agenticAPI.retaskHypothesis(pentest.id, detail.id, {
                            note: 'Reopened by the operator from the Bench.',
                          }))}
                        title="Put this angle back in the queue. It re-runs under the plan you already approved."
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {busy === 'retask'
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <RotateCcw className="h-3.5 w-3.5" />}
                        Reopen
                      </button>
                    )}
                    {/* DISMISS. The other way to clear a blocked angle: put it to
                        rest rather than retry it. `abandon` is an honest status
                        of its own ("we chose not to pursue this"), distinct from
                        refuted, so a report never reads a dismissal as a
                        disproof. Offered on blocked only; a settled outcome is
                        already at rest. */}
                    {detail.status === 'blocked' && (
                      <button
                        type="button"
                        disabled={busy === 'abandon'}
                        onClick={() => act('abandon', () =>
                          agenticAPI.abandonHypothesis(pentest.id, detail.id))}
                        title="Dismiss this angle. It is marked abandoned (not refuted) and leaves the active list; it stays in the record and can be reopened."
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {busy === 'abandon'
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Ban className="h-3.5 w-3.5" />}
                        Dismiss
                      </button>
                    )}
                    {/* A PERCENTAGE THAT DOES NOT MOVE IS WORSE THAN NO
                        PERCENTAGE. `confidence` is what the agent says about
                        its own belief, and it writes it when it CONCLUDES:
                        until then it is 0. Rendered as the one big number
                        beside a working angle, it read as progress, sat at 0%
                        for minutes, and then jumped to a verdict. The operator
                        reported it as the progress bar being fake, and he was
                        right about the only thing that mattered - it was not
                        measuring the thing it was standing next to.

                        So while an angle is under test the big number is turns
                        taken, which is a row that gets written. Confidence
                        appears once there is a verdict, where it is a real
                        statement somebody made. */}
                    {['queued', 'investigating'].includes(detail.status) ? (
                      <div className="text-right" data-testid="angle-progress">
                        <div className="text-lg font-semibold text-slate-900">
                          {/* A COUNTER PLUS A SECOND HAND. The count alone is
                              correct and steps once every couple of minutes,
                              which the operator read as frozen and said so.
                              TurnClock carries the pulse too. */}
                          <TurnClock run={activeRun} now={now} />
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">
                          {activeRun?.status === 'running'
                            ? 'turns \u00b7 time on this turn'
                            : 'turns taken'}
                        </div>
                      </div>
                    ) : detail.confidence > 0 ? (
                      <div className="text-right" data-testid="angle-confidence">
                        <div className="font-mono text-lg font-semibold text-slate-900">
                          {detail.confidence}%
                        </div>
                        <div
                          className="text-[10px] uppercase tracking-wider text-slate-400"
                          title="How sure the agent said it was when it reached this verdict. Not progress: it is written once, at the end."
                        >
                          confidence
                        </div>
                      </div>
                    ) : (
                      /* CONFIDENCE IS OPTIONAL AND USUALLY ABSENT. Measured on
                         pentest 50: four angles CONFIRMED, three of them with
                         confidence still 0, because `conclude` does not require
                         the field and the model mostly does not fill it in.

                         Printing "0%" beside a confirmed finding is the worst
                         reading available. It looks like the runtime does not
                         believe its own result, on the one screen whose job is
                         to make a measured finding arguable. The verdict is the
                         real statement here; the number was never made. */
                      <div className="text-right" data-testid="angle-verdict">
                        <div className="text-sm font-semibold capitalize text-slate-900">
                          {HYPOTHESIS_META[detail.status]?.label || detail.status}
                        </div>
                        <div
                          className="text-[10px] uppercase tracking-wider text-slate-400"
                          title="The agent recorded no confidence figure for this verdict. The evidence below is the claim; a self-reported percentage would add nothing to it."
                        >
                          verdict
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* WHY, IN WORDS, RIGHT HERE. "Blocked" alone made the operator
                    open the angle hunting for a reason that was never rendered.
                    The reason comes from the backend now (the run's blocked_reason
                    or the worker's last conclude turn); this is the readable
                    callout, not just the rail's hover tooltip. Amber for blocked
                    (an operator can act on it), muted for a settled verdict. */}
                {['blocked', 'refuted', 'abandoned'].includes(detail.status)
                  && detail.blocked_reason && (
                  <div
                    data-testid="angle-blocked-reason"
                    className={`mt-2 flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                      detail.status === 'blocked'
                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                        : 'border-slate-200 bg-slate-50 text-slate-600'
                    }`}
                  >
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      <span className="font-semibold">
                        {detail.status === 'blocked'
                          ? 'Why this is blocked: '
                          : detail.status === 'refuted'
                            ? 'Why this was ruled out: '
                            : 'Why this was dismissed: '}
                      </span>
                      {detail.blocked_reason}
                    </span>
                  </div>
                )}
                {activeRun && (
                  <div className="mt-2 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-2">
                    <Meter label="Turns" used={activeRun.turns_used} cap={activeRun.turn_cap} />
                    <Meter
                      label="Tokens"
                      used={activeRun.input_tokens + activeRun.output_tokens}
                      cap={activeRun.token_budget}
                    />
                    <span className="text-xs text-slate-500">
                      run <span className="font-medium text-slate-700">{activeRun.status}</span>
                      {activeRun.stall_count > 0 && (
                        <span className="ml-2 text-amber-700">
                          stalled {activeRun.stall_count}
                        </span>
                      )}
                    </span>
                    {activeRun.blocked_reason && (
                      <span className="text-xs text-amber-800">{activeRun.blocked_reason}</span>
                    )}
                  </div>
                )}
              </div>

              {tab === 'bench' && <EvidencePane evidence={detail.evidence} />}
              {tab === 'stream' && <StreamView turns={detail.turns} evidence={detail.evidence} />}
              {tab === 'findings' && (
                confirmed.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                    Nothing confirmed yet. A verdict only becomes a finding after it
                    survives independent review.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {confirmed.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => { setSelectedId(l.id); chooseTab('bench'); }}
                        className="block w-full rounded-lg border border-red-200 bg-red-50/40 p-3 text-left hover:bg-red-50"
                      >
                        <div className="text-sm font-semibold text-slate-900">{l.title}</div>
                        <div className="mt-0.5 text-xs text-slate-600">{l.hypothesis}</div>
                      </button>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </section>
      </div>
      )}

    </div>
  );
}
